import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "@/graphql/base-query";
import {
  CREATE_CHAT_MUTATION,
  GET_CHATS_QUERY,
  GET_CHAT_QUERY,
  SEND_MESSAGE_MUTATION,
  UPDATE_CHAT_TITLE_MUTATION,
  UPDATE_CHAT_CATEGORY_MUTATION,
  DELETE_CHAT_MUTATION,
} from "@/graphql/chat-requests";
import {
  Chat,
  ChatCreateInput,
  ChatFilterInput,
  ChatMessageInput,
  CreateChatResponse,
  GetChatsResponse,
  GetChatResponse,
  SendMessageResponse,
  UpdateChatResponse,
  DeleteChatResponse,
  ChatsResponse,
} from "@/types/chat";

export const chatApi = createApi({
  reducerPath: "api/chat",
  baseQuery,
  tagTypes: ["Chat", "ChatList"],
  endpoints: (builder) => ({
    createChat: builder.mutation<Chat, ChatCreateInput>({
      query: (input) => {
        console.log(`🔍 [Chat API] createChat called with input:`, input);
        return {
          document: CREATE_CHAT_MUTATION,
          variables: { input },
        };
      },
      onQueryStarted: async (input, { queryFulfilled }) => {
        console.log(`🔍 [Chat API] createChat mutation started with input:`, input);
        try {
          const result = await queryFulfilled;
          console.log(`✅ [Chat API] createChat mutation successful:`, result);
        } catch (error) {
          console.error(`❌ [Chat API] createChat mutation failed:`, error);
        }
      },
      invalidatesTags: ["ChatList"],
      transformResponse: (response: CreateChatResponse) => {
        console.log(`🔍 [Chat API] createChat raw response:`, response);
        const transformed = response.createChat.data;
        console.log(`🔍 [Chat API] createChat transformed response:`, transformed);
        return transformed;
      },
    }),

    getChats: builder.query<ChatsResponse, ChatFilterInput | void>({
      query: (filter = {}) => {
        console.log(`🔍 [Chat API] getChats called with filter:`, filter);
        return {
          document: GET_CHATS_QUERY,
          variables: { filter },
        };
      },
      onQueryStarted: async (filter, { queryFulfilled }) => {
        console.log(`🔍 [Chat API] getChats query started with filter:`, filter);
        try {
          const result = await queryFulfilled;
          console.log(`✅ [Chat API] getChats query successful:`, result);
        } catch (error) {
          console.error(`❌ [Chat API] getChats query failed:`, error);
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({ type: "Chat" as const, id: _id })),
              { type: "ChatList", id: "LIST" },
            ]
          : [{ type: "ChatList", id: "LIST" }],
      transformResponse: (response: GetChatsResponse) => {
        console.log(`🔍 [Chat API] getChats raw response:`, response);
        console.log(`🔍 [Chat API] getChats response type:`, typeof response);
        console.log(`🔍 [Chat API] getChats response keys:`, Object.keys(response || {}));
        
        // Try to access the data safely
        const chatsData = response?.chats;
        console.log(`🔍 [Chat API] chatsData:`, chatsData);
        console.log(`🔍 [Chat API] chatsData type:`, typeof chatsData);
        
        if (chatsData) {
          console.log(`🔍 [Chat API] chatsData keys:`, Object.keys(chatsData));
          console.log(`🔍 [Chat API] chatsData.data:`, chatsData.data);
          console.log(`🔍 [Chat API] chatsData.data type:`, typeof chatsData.data);
          console.log(`🔍 [Chat API] chatsData.data isArray:`, Array.isArray(chatsData.data));
        }
        
        // Handle different response structures
        let transformed;
        if (response?.chats) {
          transformed = response.chats;
        } else {
          // Fallback: create empty response structure
          console.warn(`⚠️ [Chat API] Unexpected response structure, creating fallback`);
          transformed = {
            data: [],
            meta: {
              countDocs: 0,
              pageNo: 1,
              pageSize: 10
            }
          };
        }
        
        console.log(`🔍 [Chat API] getChats transformed response:`, transformed);
        return transformed;
      },
    }),

    getChat: builder.query<Chat, string>({
      query: (id) => ({
        document: GET_CHAT_QUERY,
        variables: { id },
      }),
      providesTags: (_, __, id) => [{ type: "Chat", id }],
      transformResponse: (response: GetChatResponse) => response.chat.data,
    }),

    sendMessage: builder.mutation<
      Chat,
      { chatId: string; input: ChatMessageInput }
    >({
      query: ({ chatId, input }) => {
        console.log(`🔍 [Chat API] sendMessage called with chatId: ${chatId}, input:`, input);
        return {
          document: SEND_MESSAGE_MUTATION,
          variables: { chatId, input },
        };
      },
      onQueryStarted: async ({ chatId }, { queryFulfilled }) => {
        console.log(`🔍 [Chat API] sendMessage mutation started for chat ${chatId}`);
        try {
          const result = await queryFulfilled;
          console.log(`✅ [Chat API] sendMessage mutation successful:`, result);
        } catch (error) {
          console.error(`❌ [Chat API] sendMessage mutation failed for chat ${chatId}:`, error);
        }
      },
      invalidatesTags: (_, __, { chatId }) => [
        { type: "Chat", id: chatId },
        { type: "ChatList", id: "LIST" },
      ],
      transformResponse: (response: SendMessageResponse) => {
        console.log(`🔍 [Chat API] sendMessage raw response:`, response);
        const transformed = response.sendMessage.data;
        console.log(`🔍 [Chat API] sendMessage transformed response:`, transformed);
        return transformed;
      },
    }),

    updateChatTitle: builder.mutation<Chat, { id: string; title: string }>({
      query: ({ id, title }) => ({
        document: UPDATE_CHAT_TITLE_MUTATION,
        variables: { id, title },
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "Chat", id },
        { type: "ChatList", id: "LIST" },
      ],
      transformResponse: (response: UpdateChatResponse) => response.updateChat.data,
    }),

    updateChatCategory: builder.mutation<Chat, { id: string; category: string }>({
      query: ({ id, category }) => ({
        document: UPDATE_CHAT_CATEGORY_MUTATION,
        variables: { id, category },
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "Chat", id },
        { type: "ChatList", id: "LIST" },
      ],
      transformResponse: (response: UpdateChatResponse) => response.updateChat.data,
    }),

    deleteChat: builder.mutation<boolean, string>({
      query: (id) => ({
        document: DELETE_CHAT_MUTATION,
        variables: { id },
      }),
      invalidatesTags: (_, __, id) => [
        { type: "Chat", id },
        { type: "ChatList", id: "LIST" },
      ],
      transformResponse: (response: DeleteChatResponse) => response.deleteChat.data,
    }),
  }),
});

export const {
  useCreateChatMutation,
  useGetChatsQuery,
  useGetChatQuery,
  useSendMessageMutation,
  useUpdateChatTitleMutation,
  useUpdateChatCategoryMutation,
  useDeleteChatMutation,
} = chatApi;
